"use client"
import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useRef } from "react"
import { FormError } from "../ContactUs/ContactContent"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;
  padding: 61px 0;
  gap: 32px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 120px 0;
    gap: 24px;
  }
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 16px;
`

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$designC",
})<{ $designC?: boolean }>`
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    width: ${(p) => (p.$designC ? "100%" : "386px")};
  }
`

const IdText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
`

const ButtonContainer = styled.div<{ $designC?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: ${(p) => (p.$designC ? "100%" : "unset")};
`

export default function Result({
  isFormError,
  clickHandler,
  designC,
  designD,
}: {
  isFormError: FormError
  clickHandler: () => void
  designC?: boolean
  designD?: boolean
}) {
  const designCD = designC || designD
  const tInner = useTranslations("InnerPages")
  const tForms = useTranslations("Forms")
  const layoutRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (layoutRef.current) {
      layoutRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [])

  return (
    <StyledLayout ref={layoutRef} role="alert">
      {isFormError === "yes" && (
        <Image
          src={"/icons/formError.svg"}
          width={120}
          height={120}
          alt="שגיאה בשליחת הטופס"
        />
      )}
      {isFormError === "no" && (
        <Image
          src={"/icons/formSuccess.gif"}
          width={120}
          height={120}
          alt="הטופס נשלח בהצלחה"
          style={{ marginBottom: "-24px" }}
        />
      )}
      {isFormError === "yes" && (
        <TextBox>
          <Typography variant="h3" component={"h1"}>
            {designD
              ? "אירעה שגיאה בשליחת הטופס"
              : "משהו השתבש בדרך אלינו..."}
          </Typography>
          <Typography variant="body1" component={"p"}>
            {designD
              ? "אנא נסה שוב"
              : "כנראה שהיתה שגיאה בשליחת הפנייה. אפשר לנסות שוב"}
          </Typography>
        </TextBox>
      )}
      {isFormError === "no" && (
        <TextBox>
          <Typography variant="h3" component={"h1"}>
            {designD
              ? "תודה! פנייתך התקבלה בהצלחה וניצור עמך קשר בהקדם"
              : designC
                ? "עלית על הדרך לקריירה יציבה באגד, נחזור אליך עם פרטים נוספים בהקדם."
                : (
                    <>
                      תודה.
                      <br />
                      העברנו את פרטיך לטיפול
                    </>
                  )}
          </Typography>
          {!designCD && (
            <Typography variant="body1" component={"p"}>
              עלית על הדרך לקריירה באגד. נחזור אליך בקרוב (אם זה יהיה רלוונטי
              כמובן)
            </Typography>
          )}
        </TextBox>
      )}
      <ButtonContainer $designC={designCD}>
        {isFormError === "no" ? (
          <StyledButton
            variant={"primary"}
            LinkComponent={Link}
            href="/"
            $designC={designCD}
          >
            לאתר אגד
          </StyledButton>
        ) : (
          <StyledButton
            variant={"primary"}
            onClick={clickHandler}
            $designC={designCD}
          >
            ניסיון נוסף
          </StyledButton>
        )}
      </ButtonContainer>
    </StyledLayout>
  )
}
